import React, { useLayoutEffect, useMemo, useReducer, useRef } from 'react';
import {
  Location,
  UNSAFE_LocationContext,
  useLocation,
  useNavigationType,
  useOutlet,
} from 'react-router-dom';
import { Box } from '@mui/material';
import { TabPanelActiveContext } from './TabPanelActiveContext';

type CacheEntry = {
  element: React.ReactElement;
  location: Location;
};

export type TabWorkspaceHostProps = {
  /** Paths whose workspace panels stay mounted (open tabs + current route). */
  alivePaths: string[];
};

/**
 * Keeps dashboard route panels mounted while their tab is open.
 * Inactive panels are hidden (not unmounted) so filters, pagination, list data,
 * and per-panel scroll are preserved across tab switches.
 *
 * Each panel gets a frozen LocationContext while inactive so hooks like
 * useLocation / usePageState keep the tab's own path instead of the active route.
 *
 * Also provides {@link TabPanelActiveContext} so portaled Dialog/Drawer instances
 * can stay "open" in parent state without painting over another tab.
 */
const TabWorkspaceHost: React.FC<TabWorkspaceHostProps> = ({ alivePaths }) => {
  const outlet = useOutlet();
  const location = useLocation();
  const navigationType = useNavigationType();
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map());
  const [, bump] = useReducer((n: number) => n + 1, 0);

  const pathname = location.pathname;
  const aliveSet = useMemo(() => new Set(alivePaths), [alivePaths]);

  // Mutate the keep-alive cache outside render (React purity).
  useLayoutEffect(() => {
    let changed = false;

    for (const key of [...cacheRef.current.keys()]) {
      if (!aliveSet.has(key)) {
        cacheRef.current.delete(key);
        changed = true;
      }
    }

    if (outlet != null) {
      const existing = cacheRef.current.get(pathname);
      if (existing == null) {
        cacheRef.current.set(pathname, { element: outlet, location });
        changed = true;
      } else {
        // Freeze the latest location when leaving this tab later.
        existing.location = location;
      }
    }

    if (changed) {
      bump();
    }
  }, [aliveSet, pathname, outlet, location]);

  const entries = Array.from(cacheRef.current.entries());

  return (
    <Box
      sx={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {entries.map(([path, entry]) => {
        const isActive = path === pathname;
        return (
          <Box
            key={path}
            sx={{
              display: isActive ? 'block' : 'none',
              flex: isActive ? 1 : undefined,
              minHeight: 0,
              height: '100%',
              overflowY: 'auto',
              width: '100%',
            }}
            aria-hidden={!isActive}
          >
            <TabPanelActiveContext.Provider value={isActive}>
              <UNSAFE_LocationContext.Provider
                value={{
                  location: isActive ? location : entry.location,
                  navigationType,
                }}
              >
                {entry.element}
              </UNSAFE_LocationContext.Provider>
            </TabPanelActiveContext.Provider>
          </Box>
        );
      })}
    </Box>
  );
};

export default TabWorkspaceHost;
